/**
 * AccountAccessPanel — compact quick-control panel triggered from the
 * single consolidated topbar chip (Iter 334b).
 *
 * Three-card vertical overview using brand purple (#9200C7) instead of
 * black. Each card is expandable for deeper explanation. "Change account
 * type" opens an inline second step with FULL-ROW clickable cards (no
 * separate buttons required) and no staff-review gating — all three
 * tracks are self-service. After a switch the customer lands back on
 * /dashboard. "Compare features" opens an inline compact comparison
 * table that includes the Free Weekly post-trial column and highlights
 * the customer's current account type.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authedFetch } from '@/lib/auth';
import { useLanguage } from '@/lib/i18n';
import { useEntitlements } from '@/lib/entitlements';
import {
  X, Users, Building2, ShieldCheck, Sparkles, Crown, Check, ArrowRight,
  ArrowLeft, Mail, Info, Loader2, Star, ChevronDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TRACK_META = {
  personal:   { Icon: Users,       label_en: 'Personal',              label_ja: '個人',          tint: 'violet' },
  business:   { Icon: Building2,   label_en: 'Business',              label_ja: '法人',          tint: 'emerald' },
  government: { Icon: ShieldCheck, label_en: 'Government & Public-Sector', label_ja: '官公庁・公共部門',  tint: 'sky' },
};

const TINTS = {
  violet:  { ring: 'border-violet-300',  bg: 'bg-violet-50',  iconBg: 'bg-violet-100',  iconText: 'text-violet-700' },
  emerald: { ring: 'border-emerald-300', bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconText: 'text-emerald-700' },
  sky:     { ring: 'border-sky-300',     bg: 'bg-sky-50',     iconBg: 'bg-sky-100',     iconText: 'text-sky-700' },
};

// Customer-friendly labels — these MUST stay aligned with sidebar,
// Reports Hub and Plan page copy. (See FEATURE_ENTITLEMENT_MATRIX.md.)
const FRIENDLY_LABELS = {
  meisai_automation:        { en: 'MEISAI connection',  ja: 'MEISAI接続' },
  meisai_connection_health: { en: 'MEISAI connection',  ja: 'MEISAI接続' },
  weekly_reports:           { en: 'Weekly reports',     ja: '週次レポート' },
  custom_date_reports:      { en: 'Custom reports',     ja: 'カスタムレポート' },
  reimbursement_reports:    { en: 'Expense reports',    ja: '経費精算レポート' },
  bulk_reports:             { en: 'Bulk reports',       ja: '一括レポート' },
  smart_spend_alerts:       { en: 'Smart Alerts',       ja: 'スマートアラート' },
  vehicle_monitoring:       { en: 'Vehicle monitoring', ja: '車両モニタリング' },
  family_vehicle_list:      { en: 'Family vehicles',    ja: '家族の車両' },
  authorized_vehicle_registry: { en: 'Authorized vehicles', ja: '認可車両' },
  manager_review_queue:     { en: 'Manager review',     ja: 'マネージャーレビュー' },
  ai_pattern_review:        { en: 'Pattern review',     ja: 'パターンレビュー' },
};

const FEATURE_DESCRIPTIONS = {
  meisai_automation: {
    en: 'Keep your ETC usage source connected so reports can be prepared from real ETC Usage Inquiry Service (MEISAI) records.',
    ja: 'ETC利用履歴の取得元を接続し、実際のETC利用照会サービス（MEISAI）記録からレポートを作成できる状態にします。',
  },
  weekly_reports: {
    en: 'Automatic weekly PDF and CSV summaries for regular expense review.',
    ja: '定期的な経費確認に使える週次PDF・CSVサマリーです。',
  },
  custom_date_reports: {
    en: 'Choose a period, cards, and format for an on-demand report.',
    ja: '期間・カード・形式を選んで、必要なときにレポートを作成します。',
  },
  reimbursement_reports: {
    en: 'Package toll activity for expense claims without exposing raw card details.',
    ja: 'カード詳細を出さずに、経費精算向けの利用情報をまとめます。',
  },
  bulk_reports: {
    en: 'Prepare multi-card report packages for Business and approved Government & Public-Sector accounts.',
    ja: '法人および承認済み官公庁・公共部門アカウント向けに複数カードのレポートをまとめます。',
  },
  smart_spend_alerts: {
    en: 'Get notified when toll patterns need attention.',
    ja: '確認が必要な通行パターンがあった場合に通知します。',
  },
  vehicle_monitoring: {
    en: 'Review vehicle activity patterns for fleet and approved public-sector use.',
    ja: 'フリートおよび承認済み公的機関向けに車両利用パターンを確認します。',
  },
  family_vehicle_list: {
    en: 'Register plate digits that are expected to appear on family ETC reports.',
    ja: '家族利用でETCレポートに表示される想定のナンバー末尾を登録します。',
  },
  authorized_vehicle_registry: {
    en: 'Maintain approved vehicle records for Business and Government & Public-Sector review workflows.',
    ja: '法人・官公庁向けの確認フローで使う認可車両を管理します。',
  },
  manager_review_queue: {
    en: 'Route toll activity that needs manager review before reimbursement or audit export.',
    ja: '精算・監査出力の前に管理者確認が必要な利用を整理します。',
  },
};

const FEATURE_ACTIONS = {
  meisai_automation: '/dashboard/meisai-health',
  weekly_reports: '/dashboard/reports',
  custom_date_reports: '/dashboard/custom-reports',
  reimbursement_reports: '/dashboard/reimbursement',
  smart_spend_alerts: '/dashboard/spend-alerts',
  family_vehicle_list: '/dashboard/family-authorized-vehicles',
  vehicle_monitoring: '/dashboard/vehicle-monitoring',
  bulk_reports: '/dashboard/business-suite/reports',
  authorized_vehicle_registry: '/dashboard/business-suite/cards',
  manager_review_queue: '/dashboard/manager-review',
};

// Ordered list of features we surface in the modal. We filter by what
// the user can actually access, capped at 8.
const FEATURE_DISPLAY_ORDER = [
  'meisai_automation',
  'weekly_reports',
  'custom_date_reports',
  'reimbursement_reports',
  'bulk_reports',
  'smart_spend_alerts',
  'vehicle_monitoring',
  'family_vehicle_list',
  'authorized_vehicle_registry',
  'manager_review_queue',
];

// Comparison-table rows shown inside the inline "Compare features" view.
// `free_weekly` is the post-Early-Access tier all Personal customers fall
// back to after their 1-year free trial ends. Listed here so customers
// can see what they keep, and what becomes Premium-only, after the trial.
const COMPARE_ROWS = [
  { key: 'meisai_automation',    plans: ['free_weekly', 'personal', 'business', 'government'] },
  { key: 'weekly_reports',       plans: ['free_weekly', 'personal', 'business', 'government'] },
  { key: 'custom_date_reports',  plans: ['personal', 'business', 'government'] },
  { key: 'reimbursement_reports',plans: ['personal', 'business', 'government'] },
  { key: 'smart_spend_alerts',   plans: ['personal', 'business', 'government'] },
  { key: 'family_vehicle_list',  plans: ['personal'] },
  { key: 'vehicle_monitoring',   plans: ['business', 'government'] },
  { key: 'bulk_reports',         plans: ['business', 'government'] },
  { key: 'manager_review_queue', plans: ['business', 'government'] },
  { key: 'authorized_vehicle_registry', plans: ['business', 'government'] },
];

export default function AccountAccessPanel({ open, focus, onClose }) {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const ent = useEntitlements();
  const ja = lang === 'ja';
  const [data, setData] = useState(null);
  const [view, setView] = useState('overview'); // overview | compare
  // Iter 334j — `focus` prop is honoured. Topbar chip is now segmented
  // into three clickable areas; each one passes a different focus value
  // ('track', 'plan', 'access', 'compare') so the matching card opens
  // expanded without the customer having to click again inside the
  // modal.
  const [focusSection, setFocusSection] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState(null);
  const dialogRef = useRef(null);
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSwitchError(null);
    // Map the requested focus to the right initial view + expanded card.
    if (focus === 'compare') {
      setView('compare');
      setFocusSection(null);
    } else {
      setView('overview');
      setFocusSection(focus || null);
    }
    let cancel = false;
    (async () => {
      try {
        const r = await authedFetch(`${API}/api/customer/account-type`, { credentials: 'include' });
        if (r.ok && !cancel) setData(await r.json());
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [open, focus]);

  // Iter 334j — Lock background scroll while the modal is open so the
  // page never appears to "scroll behind" the centred panel.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // A11y: Escape closes, focus enters the dialog on open and returns to the
  // trigger on close, and Tab is trapped inside the dialog.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const node = dialogRef.current;
    const t = setTimeout(() => { node?.focus(); }, 0);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== 'Tab' || !node) return;
      const f = node.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      const r = restoreFocusRef.current;
      if (r && typeof r.focus === 'function') r.focus();
    };
  }, [open, onClose]);

  const currentTrack = (data?.current || 'personal').toLowerCase();
  const trackMeta = TRACK_META[currentTrack] || TRACK_META.personal;
  const trackTint = TINTS[trackMeta.tint];

  const friendlyLabel = (key) => {
    const m = FRIENDLY_LABELS[key];
    if (m) return ja ? m.ja : m.en;
    const f = ent.features?.[key];
    return ja ? (f?.label_ja || key) : (f?.label_en || key);
  };

  const includedFeatures = useMemo(() => {
    if (!ent.ready) return [];
    return FEATURE_DISPLAY_ORDER.filter((k) => ent.can(k)).slice(0, 8);
  }, [ent]);

  // Short customer summary line (no backend keys).
  const summaryLine = useMemo(() => {
    const trackLabel = ja ? trackMeta.label_ja : trackMeta.label_en;
    if (ja) {
      return `先行受付期間中、${trackLabel}アカウントをプレミアム機能でご利用中です。`;
    }
    return `You are using a ${trackLabel} account with Premium access during Early Access.`;
  }, [ja, trackMeta]);

  async function performSwitch(target) {
    setSwitching(true);
    setSwitchError(null);
    try {
      const r = await authedFetch(`${API}/api/customer/account-type/switch`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: target, confirm: true }),
      });
      if (!r.ok) {
        let msg = ja ? '切り替えに失敗しました。' : 'Could not switch account type.';
        try {
          const e = await r.json();
          if (e?.detail?.message) msg = e.detail.message;
          else if (typeof e?.detail === 'string') msg = e.detail;
        } catch { /* swallow */ }
        setSwitchError(msg);
        setSwitching(false);
        return;
      }
      // Switching to a new track changes what the sidebar + dashboard
      // render — head back to the overview and force a full reload so
      // the entitlement hook and sidebar pick up the new account type.
      onClose?.();
      window.location.assign('/dashboard');
    } catch {
      setSwitchError(ja ? '通信エラーが発生しました。' : 'Network error — please try again.');
      setSwitching(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="account-access-panel"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={ja ? 'アカウントとご利用状況' : 'Your account & access'}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden outline-none"
        data-testid="account-access-panel-dialog"
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
          <div className="flex items-center gap-2">
            {view !== 'overview' && (
              <button
                type="button"
                onClick={() => { setView('overview'); setSwitchError(null); }}
                className="text-slate-500 hover:text-slate-800"
                data-testid="account-access-panel-back"
                aria-label={ja ? '戻る' : 'Back'}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h2 className="text-base font-bold text-slate-900">
              {view === 'compare' ? (ja ? '機能の比較' : 'Compare features')
              :                      (ja ? 'アカウントとご利用状況' : 'Your account & access')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 inline-flex items-center justify-center min-h-[44px] min-w-[44px] -mr-2"
            data-testid="account-access-panel-close"
            data-icon-only
            aria-label={ja ? '閉じる' : 'Close'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── OVERVIEW VIEW ─────────────────────────────────────────── */}
        {view === 'overview' && (
          <>
            <div className="p-5 space-y-3.5 flex-1 min-h-0 overflow-y-auto" data-testid="account-access-panel-scroll-area">
              <p className="text-sm text-slate-700 leading-relaxed" data-testid="account-access-panel-intro">
                {ja
                  ? 'アカウントタイプは、ダッシュボードで使えるツールを決めます。プレミアムはご利用いただける機能のレベル、先行受付は1年間無料で利用できる期間を指します。'
                  : 'Your account type controls which tools are available in your dashboard. Premium controls your access level, and Early Access gives you 1 year free.'}
              </p>

              <div className="space-y-2" data-testid="account-access-panel-cards">
                {/* Account Type — expandable card with inline track switcher
                    so the customer can change their track without leaving
                    the modal (Iter 334e — per direct customer feedback). */}
                <InlineAccountTypeCard
                  ja={ja}
                  data={data}
                  currentTrack={currentTrack}
                  trackMeta={trackMeta}
                  trackTint={trackTint}
                  switching={switching}
                  switchError={switchError}
                  defaultOpen={focusSection === 'track'}
                  onPerform={performSwitch}
                />
                <ExpandableInfoCard
                  testId="account-access-panel-plan-card"
                  Icon={Crown}
                  iconWrapClass="bg-[#9200C7]/10 text-[#9200C7]"
                  borderClass="border-[#9200C7]/30 bg-[#9200C7]/5"
                  eyebrow={ja ? 'プランレベル' : 'Plan level'}
                  title={ja ? 'プレミアム' : 'Premium'}
                  oneLiner={ja ? '機能のレベルを決定' : 'Controls feature level'}
                  details={[
                    ja
                      ? '先行受付期間中、すべてのプレミアム機能をご利用いただけます。'
                      : 'During Early Access you get every Premium feature.',
                    ja
                      ? '法人・官公庁向けの追加機能は、アカウントタイプによって決まります。'
                      : 'Business and Government extras still depend on your account type.',
                    ja
                      ? '先行受付終了後、個人アカウントは無料週次プランへ自動移行します。'
                      : 'After Early Access, Personal accounts move to the Free Weekly plan automatically.',
                  ]}
                  defaultOpen={focusSection === 'plan'}
                />
                <ExpandableInfoCard
                  testId="account-access-panel-access-card"
                  Icon={Sparkles}
                  iconWrapClass="bg-fuchsia-100 text-fuchsia-700"
                  borderClass="border-fuchsia-300 bg-fuchsia-50"
                  eyebrow={ja ? '先行受付' : 'Early Access'}
                  title={ja ? '1年間無料' : '1 year free'}
                  oneLiner={ja ? '無料でご利用いただける期間' : '1 year free access'}
                  details={[
                    ja
                      ? '先行受付メンバーは、プレミアム機能を1年間無料でご利用いただけます。'
                      : "As an Early Access member, you get 1 year of Premium features at no cost.",
                    ja
                      ? '料金は表示されません。ご請求も発生しません。'
                      : 'No billing is shown and no charges are made.',
                    ja
                      ? '1年経過後、個人アカウントは無料週次プランへ自動移行します。'
                      : 'After 1 year, Personal accounts continue on the Free Weekly plan automatically.',
                  ]}
                  defaultOpen={focusSection === 'access'}
                />
              </div>

              {/* Current-account summary line */}
              <div
                className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700"
                data-testid="account-access-panel-summary"
              >
                {summaryLine}
              </div>

              {/* Included features — entitlement-driven */}
              {ent.ready && includedFeatures.length > 0 && (
                <div className="rounded-xl border border-slate-200 p-3" data-testid="account-access-panel-features-list">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    {ja ? 'このアカウントで使える機能の一部' : 'Some features included for your account'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-slate-700">
                    {includedFeatures.map((k) => {
                      const action = FEATURE_ACTIONS[k];
                      const desc = FEATURE_DESCRIPTIONS[k];
                      return (
                        <div key={k} className="rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2" data-testid={`account-access-panel-feature-${k}`}>
                          <div className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.6} />
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-slate-800 leading-snug">{friendlyLabel(k)}</p>
                              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug" data-testid={`account-access-panel-feature-${k}-description`}>
                                {ja ? desc?.ja : desc?.en}
                              </p>
                              {action && (
                                <Link
                                  to={action}
                                  onClick={onClose}
                                  className="inline-flex items-center gap-1 text-[10px] font-bold text-[#9200C7] hover:text-[#7a00a8] mt-1"
                                  data-testid={`account-access-panel-feature-${k}-open`}
                                >
                                  {ja ? '開く' : 'Open'}
                                  <ArrowRight className="w-2.5 h-2.5" strokeWidth={2.6} />
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setView('compare')}
                    className="mt-2 text-xs font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2"
                    data-testid="account-access-panel-view-all-features"
                  >
                    {ja ? 'すべての機能を比較' : 'View all features →'}
                  </button>
                </div>
              )}
            </div>

            {/* Footer — primary actions */}
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => { onClose?.(); navigate('/dashboard/support'); }}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
                data-testid="account-access-panel-support"
              >
                <Mail className="w-3 h-3" strokeWidth={2.2} />
                {ja ? 'サポートに連絡' : 'Contact support'}
              </button>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setView('compare')}
                  className="inline-flex items-center gap-1.5 px-3 h-11 sm:h-9 rounded-md text-sm font-bold text-white bg-[#9200C7] hover:bg-[#7a00a8]"
                  data-testid="account-access-panel-features"
                >
                  {ja ? '機能を比較' : 'Compare features'}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── COMPARE VIEW ────────────────────────────────────────── */}
        {view === 'compare' && (
          <CompareFeaturesView
            ja={ja}
            currentTrack={currentTrack}
            friendlyLabel={friendlyLabel}
            canFeature={(k) => ent.ready && (ent.is_admin || ent.can(k))}
            onAction={onClose}
            onViewFullPlan={() => { onClose?.(); navigate('/dashboard/plan'); }}
          />
        )}
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────── */

function InlineAccountTypeCard({
  ja, data, currentTrack, trackMeta, trackTint,
  switching, switchError, defaultOpen, onPerform,
}) {
  // The Account Type card replaces both the old static "info" card and
  // the old standalone "Change account type" view (Iter 334e). It is
  // collapsed by default — clicking the header expands it and shows
  // inline switcher rows for the three tracks (Personal, Business,
  // Government & Public-Sector). The customer never has to leave the modal
  // to change tracks.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  const personalEligible = data?.eligibility?.personal?.allowed !== false;
  const meisaiCount = data?.meisai_account_count ?? 0;

  const targets = [
    {
      id: 'personal',
      meta: TRACK_META.personal,
      requiresReview: false,
      eligible: personalEligible,
      consequence_en: currentTrack === 'business'
        ? 'Some Business tools will be hidden after switching.'
        : 'Switches your dashboard to the Personal toolset.',
      consequence_ja: currentTrack === 'business'
        ? '一部の法人向けツールは表示されなくなります。'
        : 'ダッシュボードを個人向けツールに切り替えます。',
      block_en: personalEligible ? null
        : `You have ${meisaiCount} ETC Usage Inquiry Service (MEISAI) accounts. Personal allows 1 — disconnect extras first.`,
      block_ja: personalEligible ? null
        : `ETC利用照会サービス（MEISAI）が${meisaiCount}件接続中です。個人プランは1件まで。先に切断してください。`,
    },
    {
      id: 'business',
      meta: TRACK_META.business,
      requiresReview: false,
      eligible: true,
      consequence_en: 'Business tools (Bulk Reports, Vehicle Monitoring, Manager Review) appear.',
      consequence_ja: '法人向けツール（一括レポート、車両モニタリング、マネージャーレビュー）が表示されます。',
    },
    {
      id: 'government',
      meta: TRACK_META.government,
      requiresReview: false,
      eligible: true,
      consequence_en: 'Government & Public-Sector tools (audit trails, multi-account, vehicle monitoring) appear.',
      consequence_ja: '官公庁・公共部門向けツール（取扱履歴、複数アカウント、車両モニタリング）が表示されます。',
    },
  ];

  const headerLabel = ja ? trackMeta.label_ja : trackMeta.label_en;

  return (
    <div
      className={`rounded-xl border-2 ${trackTint.ring} ${trackTint.bg} transition-shadow hover:shadow-md`}
      data-testid="account-access-panel-track-card"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9200C7] rounded-xl"
        aria-expanded={open}
        data-testid="account-access-panel-track-card-toggle"
      >
        <div className={`w-10 h-10 rounded-lg ${trackTint.iconBg} flex items-center justify-center flex-shrink-0`}>
          <trackMeta.Icon className={`w-4 h-4 ${trackTint.iconText}`} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {ja ? 'アカウントタイプ' : 'Account type'}
          </p>
          <p className="text-sm font-extrabold text-slate-900 leading-tight">{headerLabel}</p>
          <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">
            {ja ? 'クリックして切り替え可能なタイプを表示' : 'Click to see switchable types'}
          </p>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180 text-[#9200C7]' : ''}`}
          strokeWidth={2.2}
        />
      </button>

      {open && (
        <div className="border-t border-slate-200/70 px-3 py-3 space-y-2 bg-white/70 rounded-b-xl" data-testid="account-access-panel-track-card-details">
          {switchError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-1.5 text-[11px] text-rose-800" data-testid="account-access-panel-switch-error">
              {switchError}
            </div>
          )}
          {targets.map((t) => {
            const isCurrent = t.id === currentTrack;
            const tint = TINTS[t.meta.tint];
            const consequence = ja ? t.consequence_ja : t.consequence_en;
            const block = ja ? t.block_ja : t.block_en;
            const clickable = !isCurrent && t.eligible && !switching;
            const handleSelect = () => { if (clickable) onPerform(t.id); };
            return (
              <div
                key={t.id}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={handleSelect}
                onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleSelect(); } }}
                className={[
                  'group rounded-lg border px-3 py-2 transition-all flex items-start gap-2.5',
                  isCurrent
                    ? `${tint.ring} ${tint.bg} ring-2 ring-[#9200C7]/40`
                    : 'border-slate-200 bg-white',
                  clickable ? 'hover:border-[#9200C7] hover:bg-[#9200C7]/5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9200C7]' : '',
                  !t.eligible ? 'opacity-70' : '',
                ].join(' ')}
                data-testid={`account-access-panel-change-${t.id}`}
              >
                <div className={`w-7 h-7 rounded-md ${tint.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <t.meta.Icon className={`w-3.5 h-3.5 ${tint.iconText}`} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs font-bold text-slate-900">{ja ? t.meta.label_ja : t.meta.label_en}</p>
                    {isCurrent && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-[#9200C7] text-white">
                        {ja ? '現在' : 'Current'}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">{consequence}</p>
                  {block && (
                    <p className="text-[10px] text-rose-700 mt-1 inline-flex items-start gap-1">
                      <Info className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />{block}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 self-center">
                  {isCurrent ? (
                    <span className="inline-flex items-center px-1.5 h-6 text-[10px] font-semibold text-slate-500">
                      {ja ? '利用中' : 'In use'}
                    </span>
                  ) : !t.eligible ? (
                    <span className="inline-flex items-center px-1.5 h-6 text-[10px] font-semibold text-slate-400">
                      {ja ? '利用不可' : 'Locked'}
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-0.5 px-2 h-7 rounded text-[10px] font-bold bg-[#9200C7] text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`account-access-panel-switch-${t.id}`}
                    >
                      {switching ? (
                        <Loader2 className="w-2.5 h-2.5 animate-spin" />
                      ) : (
                        <>
                          {ja ? '変更' : 'Switch'}
                          <ArrowRight className="w-2.5 h-2.5" strokeWidth={2.6} />
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function CompareFeaturesView({ ja, currentTrack, friendlyLabel, canFeature, onAction, onViewFullPlan }) {
  // The customer's current column gets a soft highlight + star.
  // During Early Access everyone is on Premium, but their account
  // *type* maps to the highlighted column.
  const currentCol = currentTrack; // 'personal' | 'business' | 'government'
  const columns = [
    { id: 'free_weekly', Icon: Sparkles,    iconClass: 'text-slate-500',   label_en: 'Free Weekly', label_ja: '無料週次', sub_en: 'Post-trial', sub_ja: '体験後' },
    { id: 'personal',    Icon: Users,       iconClass: 'text-violet-700',  label_en: 'Personal',    label_ja: '個人',    sub_en: 'Premium',   sub_ja: 'プレミアム' },
    { id: 'business',    Icon: Building2,   iconClass: 'text-emerald-700', label_en: 'Business',    label_ja: '法人',    sub_en: 'Premium',   sub_ja: 'プレミアム' },
    { id: 'government',  Icon: ShieldCheck, iconClass: 'text-sky-700',     label_en: 'Gov/Public',     label_ja: '官公庁',  sub_en: 'Premium',   sub_ja: 'プレミアム' },
  ];

  return (
    <>
      <div className="p-5 space-y-3 flex-1 min-h-0 overflow-y-auto" data-testid="account-access-panel-compare-scroll-area">
        <p className="text-sm text-slate-700 leading-relaxed">
          {ja
            ? '先行受付期間中は、プレミアム機能を1年間無料でご利用いただけます。1年経過後、個人アカウントは自動的に「無料週次」プランへ移行します。'
            : "You're part of Early Access — you get 1 year of Premium free. After 1 year, Personal accounts automatically move to the Free Weekly plan shown below."}
        </p>

        <div className="rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs" data-testid="account-access-panel-compare-table">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="text-left font-semibold px-3 py-2">{ja ? '機能' : 'Feature'}</th>
                {columns.map((c) => {
                  const isCurrent = c.id === currentCol;
                  return (
                    <th
                      key={c.id}
                      className={`text-center font-semibold px-1.5 py-2 w-[68px] ${isCurrent ? 'bg-amber-50' : ''}`}
                      data-testid={`account-access-panel-compare-col-${c.id}`}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        {isCurrent && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-200 text-amber-900">
                            <Star className="w-2.5 h-2.5 fill-amber-700 text-amber-700" strokeWidth={2.6} />
                            {ja ? '現在' : 'You'}
                          </span>
                        )}
                        <c.Icon className={`w-3.5 h-3.5 ${c.iconClass}`} strokeWidth={2.2} />
                        <span className="leading-tight">{ja ? c.label_ja : c.label_en}</span>
                        <span className="text-[9px] font-medium text-slate-400 leading-tight">
                          {ja ? c.sub_ja : c.sub_en}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, idx) => (
                <tr
                  key={row.key}
                  className={`border-t border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}
                  data-testid={`account-access-panel-compare-row-${row.key}`}
                >
                  <td className="px-3 py-2 text-slate-800 font-medium min-w-[220px]">
                    <div>
                      <p className="font-bold leading-tight">{friendlyLabel(row.key)}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 leading-snug" data-testid={`account-access-panel-compare-row-${row.key}-description`}>
                        {ja ? FEATURE_DESCRIPTIONS[row.key]?.ja : FEATURE_DESCRIPTIONS[row.key]?.en}
                      </p>
                      {canFeature?.(row.key) && FEATURE_ACTIONS[row.key] ? (
                        <Link
                          to={FEATURE_ACTIONS[row.key]}
                          onClick={onAction}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-[#9200C7] hover:text-[#7a00a8] mt-1"
                          data-testid={`account-access-panel-compare-row-${row.key}-open`}
                        >
                          {ja ? '開く' : 'Open'}
                          <ArrowRight className="w-2.5 h-2.5" strokeWidth={2.6} />
                        </Link>
                      ) : (
                        <span
                          className="inline-flex items-center text-[10px] font-semibold text-slate-400 mt-1"
                          data-testid={`account-access-panel-compare-row-${row.key}-locked`}
                        >
                          {ja ? 'アカウントタイプにより利用可' : 'Available by account type'}
                        </span>
                      )}
                    </div>
                  </td>
                  {columns.map((c) => {
                    const isCurrent = c.id === currentCol;
                    const included = row.plans.includes(c.id);
                    return (
                      <td key={c.id} className={`text-center px-1.5 py-1.5 ${isCurrent ? 'bg-amber-50/60' : ''}`}>
                        {included ? (
                          <Check
                            className={`w-3.5 h-3.5 mx-auto ${isCurrent ? 'text-emerald-700' : 'text-emerald-500'}`}
                            strokeWidth={2.6}
                          />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 space-y-1">
          <p>
            <b>{ja ? '無料週次' : 'Free Weekly'}</b>
            {ja
              ? '：先行受付終了後の標準プラン。ETC利用照会サービス（MEISAI）接続と週次レポートは引き続きご利用いただけます。'
              : ' — the standard plan after Early Access. ETC Usage Inquiry Service (MEISAI) connection and Weekly reports stay free.'}
          </p>
          <p>
            {ja
              ? '官公庁・公共部門アカウントは、ご利用前に必要な手続きをお願いする場合があります。'
              : 'Government & Public-Sector accounts may have additional requirements before live deployment.'}
          </p>
        </div>
      </div>

      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onViewFullPlan}
          className="inline-flex items-center gap-1.5 px-3 h-11 sm:h-9 rounded-md text-sm font-semibold border border-slate-300 text-slate-800 hover:border-[#9200C7] hover:text-[#9200C7]"
          data-testid="account-access-panel-view-full-plan"
        >
          {ja ? '詳細なプランページを開く' : 'View full plan page'}
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
        </button>
      </div>
    </>
  );
}


/* ─────────────────────────────────────────────────────────────────── */

/**
 * ExpandableInfoCard — single vertical card used in the overview view.
 *
 * Each card carries the eyebrow label, headline value and a one-liner
 * explanation. Clicking the row reveals 2-3 short detail bullets so the
 * modal stays compact by default but the curious customer can drill in
 * without leaving the panel.
 */
function ExpandableInfoCard({
  testId, Icon, iconWrapClass, borderClass, eyebrow, title, oneLiner, details, defaultOpen,
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  return (
    <div
      className={`rounded-xl border-2 ${borderClass} transition-shadow hover:shadow-md`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9200C7] rounded-xl"
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        <div className={`w-10 h-10 rounded-lg ${iconWrapClass} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {eyebrow}
          </p>
          <p className="text-sm font-extrabold text-slate-900 leading-tight">{title}</p>
          <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">{oneLiner}</p>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180 text-[#9200C7]' : ''}`}
          strokeWidth={2.2}
        />
      </button>
      {open && (
        <ul
          className="border-t border-slate-200/70 px-4 py-3 space-y-1.5 text-xs text-slate-700 bg-white/60 rounded-b-xl"
          data-testid={`${testId}-details`}
        >
          {details.map((d, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-[#9200C7] flex-shrink-0" />
              <span className="leading-snug">{d}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
